import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const publicHttp = axios.create({ baseURL: `${API}/api`, timeout: 180000 });

export default function AIInterviewSession() {
  const token = window.location.pathname.replace(/^\/interview\//, "");
  const [interview, setInterview] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [stage, setStage] = useState("intro"); // "intro" | "recording" | "review" | "submitting" | "done"
  const [currentQ, setCurrentQ] = useState(0);
  const [recorded, setRecorded] = useState({}); // { qIndex: { blob, url, uploaded } }
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [permError, setPermError] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    publicHttp.get(`/ai-interviews/session/${token}`)
      .then((r) => setInterview(r.data))
      .catch((e) => setLoadError(e.response?.data?.detail || e.message));
  }, [token]);

  // Tick a clock while recording so the elapsed time UI updates.
  useEffect(() => {
    if (stage !== "recording" || !startedAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [stage, startedAt]);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const beginInterview = async () => {
    setPermError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        await videoRef.current.play();
      }
      setStage("recording");
      setStartedAt(Date.now());
    } catch (e) {
      setPermError(
        "We couldn't access your camera or microphone. Please allow permission in your browser and try again."
      );
    }
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mimeCandidates = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"];
    const mime = mimeCandidates.find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || "";
    const recorder = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : undefined);
    recorderRef.current = recorder;
    recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mime || "video/webm" });
      const url = URL.createObjectURL(blob);
      setRecorded((prev) => ({ ...prev, [currentQ]: { blob, url, uploaded: false } }));
    };
    recorder.start(200);
    setRecording(true);
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    setRecording(false);
  };

  const uploadCurrent = useCallback(async () => {
    const item = recorded[currentQ];
    if (!item?.blob) return;
    setUploading(true);
    setErr(null);
    try {
      const fd = new FormData();
      fd.append("questionIndex", String(currentQ));
      const ext = item.blob.type.includes("mp4") ? "mp4" : "webm";
      fd.append("file", item.blob, `q${currentQ}.${ext}`);
      await publicHttp.post(`/ai-interviews/session/${token}/answer`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRecorded((prev) => ({ ...prev, [currentQ]: { ...prev[currentQ], uploaded: true } }));
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setUploading(false); }
  }, [currentQ, recorded, token]);

  const nextQuestion = async () => {
    if (!recorded[currentQ]?.uploaded) {
      await uploadCurrent();
    }
    if (currentQ + 1 < (interview?.questions?.length ?? 0)) {
      setCurrentQ(currentQ + 1);
    } else {
      setStage("review");
    }
  };

  const submitAll = async () => {
    setSubmitting(true);
    setErr(null);
    // Upload any remaining un-uploaded answers first
    for (let i = 0; i < (interview?.questions?.length ?? 0); i++) {
      if (recorded[i] && !recorded[i].uploaded) {
        setCurrentQ(i);
        // eslint-disable-next-line no-await-in-loop
        await uploadCurrent();
      }
    }
    try {
      await publicHttp.post(`/ai-interviews/session/${token}/complete`);
      stopStream();
      setStage("done");
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setSubmitting(false); }
  };

  useEffect(() => () => stopStream(), []);

  if (loadError) return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6" data-testid="ai-int-load-error">
      <div className="card p-6 max-w-md text-center">
        <div className="font-display text-xl font-bold text-red-700">Interview link not available</div>
        <p className="text-sm text-slate-600 mt-2">{loadError}</p>
      </div>
    </div>
  );
  if (!interview) return (
    <div className="min-h-screen grid place-items-center bg-slate-50 text-slate-400 text-sm">Loading your interview…</div>
  );

  const questions = interview.questions || [];
  const elapsedMin = startedAt ? Math.floor((now - startedAt) / 60000) : 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ai-int-page">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="w-8 h-8 rounded-sm grid place-items-center text-white font-display font-bold" style={{ background: "#002FA7" }}>V</div>
          <div>
            <div className="font-display font-bold">Vaku HCM</div>
            <div className="text-[11px] text-slate-500">AI Video Interview</div>
          </div>
        </div>

        {stage === "intro" && (
          <div className="card p-6" data-testid="ai-int-intro">
            <h1 className="font-display text-2xl font-bold">Hi {interview.candidateName || "there"} 👋</h1>
            <p className="text-slate-600 mt-2">
              This is a short video interview for the <strong>{interview.jobTitle}</strong> role. You&apos;ll answer {questions.length} questions on camera.
              You control the pacing — record, review, and re-record any answer before submitting.
            </p>
            <ul className="mt-4 text-sm text-slate-700 space-y-1">
              <li>• Recommended time: {interview.timeLimitMinutes} minutes total</li>
              <li>• You&apos;ll be asked for camera + microphone permission</li>
              <li>• Best on Chrome / Edge on desktop</li>
            </ul>
            {permError && <div className="mt-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">{permError}</div>}
            <button
              data-testid="ai-int-begin"
              className="btn btn-primary mt-5"
              onClick={beginInterview}
            >
              I&apos;m ready — start
            </button>
          </div>
        )}

        {stage === "recording" && (
          <div className="card p-6" data-testid="ai-int-recording">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs text-slate-500">
                Question <strong>{currentQ + 1}</strong> of {questions.length} · {elapsedMin} min elapsed
              </div>
              <div className="flex gap-1">
                {questions.map((_, i) => (
                  <div key={i} className={`h-1.5 w-6 rounded ${i < currentQ ? "bg-emerald-500" : i === currentQ ? "bg-[#002FA7]" : "bg-slate-200"}`} />
                ))}
              </div>
            </div>
            <div className="text-lg font-display font-semibold mb-4">{questions[currentQ]}</div>
            <video
              ref={videoRef}
              className="w-full rounded bg-black aspect-video"
              autoPlay
              muted
              playsInline
              data-testid="ai-int-video"
            />
            {recorded[currentQ]?.url && !recording && (
              <div className="mt-3">
                <div className="label-eyebrow !text-[10px] mb-1">Preview your answer</div>
                <video className="w-full rounded" src={recorded[currentQ].url} controls />
              </div>
            )}
            {err && <div className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="ai-int-error">{err}</div>}
            <div className="mt-4 flex flex-wrap gap-2">
              {!recording && !recorded[currentQ] && (
                <button data-testid="ai-int-record" className="btn btn-primary" onClick={startRecording}>Start recording</button>
              )}
              {recording && (
                <button data-testid="ai-int-stop" className="btn btn-outline !text-red-700 !border-red-300 hover:!bg-red-50" onClick={stopRecording}>Stop</button>
              )}
              {!recording && recorded[currentQ] && (
                <>
                  <button data-testid="ai-int-rerecord" className="btn btn-outline" onClick={() => { setRecorded((p) => { const c = { ...p }; delete c[currentQ]; return c; }); }}>Re-record</button>
                  <button data-testid="ai-int-next" className="btn btn-primary" onClick={nextQuestion} disabled={uploading}>
                    {uploading ? "Uploading…" : (currentQ + 1 < questions.length ? "Save & next" : "Save & review")}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {stage === "review" && (
          <div className="card p-6" data-testid="ai-int-review">
            <h2 className="font-display text-xl font-bold">Review before submitting</h2>
            <p className="text-slate-600 text-sm mt-1">Once you submit, Vaku will transcribe your videos and share them with the hiring team.</p>
            <ol className="mt-4 space-y-2">
              {questions.map((q, i) => (
                <li key={i} className="border rounded p-3">
                  <div className="text-sm font-medium">Q{i + 1}. {q}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    {recorded[i]?.uploaded ? "✓ Uploaded" : recorded[i] ? "Recorded (upload pending)" : "Not answered"}
                  </div>
                </li>
              ))}
            </ol>
            {err && <div className="mt-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">{err}</div>}
            <div className="mt-5 flex gap-2">
              <button className="btn btn-outline" onClick={() => setStage("recording")}>Back</button>
              <button data-testid="ai-int-submit" className="btn btn-primary" onClick={submitAll} disabled={submitting}>
                {submitting ? "Submitting…" : "Submit interview"}
              </button>
            </div>
          </div>
        )}

        {stage === "done" && (
          <div className="card p-6 text-center" data-testid="ai-int-done">
            <div className="text-5xl mb-2">✅</div>
            <h2 className="font-display text-2xl font-bold">Thanks — your interview is in.</h2>
            <p className="text-slate-600 mt-2">
              The hiring team will review your recordings and get back to you. You can close this tab.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
